<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="body-1 mb-3" role="heading" aria-level="2">
        <v-icon size="24" color="error" class="pb-1" aria-hidden="true">mdi-backup-restore</v-icon>
        <span class="ml-2">Rebuild Server</span>
      </div>

      <v-alert type="warning" border="left" dense class="kvm-rebuild__warning mb-4">
        Rebuilding <strong>destroys this server's disk and everything on it</strong>, then
        installs a fresh operating system or application. Your IP address and region are
        kept. This cannot be undone.
      </v-alert>

      <div v-if="loadingCatalog" class="py-6 text-center">
        <v-progress-circular indeterminate color="primary" size="32" width="3" aria-label="Loading install options" />
      </div>

      <template v-else>
        <div id="kvm-rebuild-what" class="subtitle-2 mb-2">What to install</div>
        <v-radio-group v-model="mode" hide-details class="mt-0 mb-3" aria-labelledby="kvm-rebuild-what">
          <v-radio value="same">
            <template v-slot:label>
              <span>Reinstall the current OS</span>
              <span class="kvm-secondary ml-1">({{ server.os }})</span>
            </template>
          </v-radio>
          <v-radio value="choose" label="Install a different OS or application" />
        </v-radio-group>

        <kvm-catalog-picker
          v-if="mode === 'choose'"
          :operating-systems="catalog.operating_systems"
          :apps="catalog.apps"
          v-model="selection"
          confirm-label="Use this"
          class="mb-2"
        />

        <!-- Domain — required when (re)installing an app (app URL / SSL / cloud-init). -->
        <div id="kvm-rebuild-domain" class="subtitle-2 mb-2 mt-2">
          Domain <span class="kvm-secondary">{{ domainRequired ? '(required for app installs)' : '(optional)' }}</span>
        </div>
        <div class="d-flex align-start" style="max-width: 560px;">
          <v-text-field
            v-model="domainInput"
            placeholder="app.example.com"
            outlined dense persistent-hint
            :hint="domainHint"
            :error="domainRequired && !domainInput.trim()"
            aria-labelledby="kvm-rebuild-domain"
            class="mr-2"
          />
          <gg-btn small tile outlined color="primary" class="mt-1 kvm-aa-text" v-if="suggestedDomain"
                  aria-label="Generate a free subdomain" @click="generateDomain">
            <v-icon left small aria-hidden="true">mdi-auto-fix</v-icon>Generate
          </gg-btn>
        </div>
        <kvm-domain-check :entitlement-id="server.id" :domain="domainInput" class="mt-1 mb-2" />

        <!-- SSH keys to install on the rebuilt server (defaults to current set) -->
        <v-divider class="mt-2 mb-3" />
        <kvm-ssh-keys :entitlement-id="server.id" selectable v-model="sshKeyIds" class="mb-4" />

        <gg-btn color="error" class="kvm-danger-btn mt-2" :disabled="!canRebuild"
                aria-label="Rebuild server" @click="confirmDialog = true">
          <v-icon left small aria-hidden="true">mdi-backup-restore</v-icon>Rebuild server
        </gg-btn>
      </template>

      <!-- Type-to-confirm dialog — guards against accidental data loss. -->
      <v-dialog v-model="confirmDialog" max-width="480" persistent>
        <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-rebuild-confirm-title">
          <gg-card-text class="pa-4">
            <div id="kvm-rebuild-confirm-title" class="body-1 mb-2" role="heading" aria-level="2">
              <v-icon color="error" class="pb-1" aria-hidden="true">mdi-alert</v-icon>
              <span class="ml-1">Confirm rebuild</span>
            </div>
            <p class="mb-3">
              This permanently erases all data on <strong>{{ server.hostname }}</strong>
              and reinstalls it. To continue, type <strong>REBUILD</strong> below.
            </p>
            <v-text-field
              v-model="confirmText"
              outlined dense hide-details
              placeholder="REBUILD"
              aria-label="Type REBUILD to confirm"
              autofocus
              class="mb-4"
              @keyup.enter="confirmText === 'REBUILD' && rebuild()"
            />
            <div class="text-right">
              <gg-btn text class="mr-2 kvm-aa-text" :disabled="submitting" @click="closeConfirm">Cancel</gg-btn>
              <gg-btn color="error" class="kvm-danger-btn" :loading="submitting"
                      :disabled="confirmText !== 'REBUILD'" aria-label="Rebuild now" @click="rebuild">
                Rebuild now
              </gg-btn>
            </div>
            <span class="kvm-sr-only" aria-live="assertive">{{ submitting ? 'Starting rebuild.' : '' }}</span>
          </gg-card-text>
        </gg-card>
      </v-dialog>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'
import KvmSshKeys from '@/views/services/hosting/kvm/KvmSshKeys'
import KvmCatalogPicker from '@/views/services/hosting/kvm/KvmCatalogPicker'
import KvmDomainCheck from '@/views/services/hosting/kvm/KvmDomainCheck'

export default {
  name: 'HostingKvmRebuild',
  components: { KvmSshKeys, KvmCatalogPicker, KvmDomainCheck },
  props: {
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // Supplied by KvmServiceView so a rebuild can trigger a re-fetch (which flips
    // the view to the provisioning spinner).
    onRefresh: { type: Function, default: null },
  },
  data: () => ({
    loadingCatalog: true,
    catalog: { operating_systems: [], apps: [] },
    mode: 'same', // same | choose
    selection: null, // { kind, slug, options } from the catalog picker
    sshKeyIds: [], // keys to inject on rebuild (defaults to the current set)
    domainInput: '', // domain override; sent only when changed from the current value
    confirmDialog: false,
    confirmText: '',
    submitting: false,
  }),
  computed: {
    // Reinstalling/choosing an app requires a domain; a bare OS doesn't.
    domainRequired () {
      if (this.mode === 'same') return !!this.server.app
      return !!(this.selection && this.selection.kind === 'app')
    },
    // Only point at Generate when the API actually offered a subdomain (the button
    // is hidden otherwise).
    domainHint () {
      const generate = this.suggestedDomain ? ' Click Generate for a free subdomain.' : ''
      return this.domainRequired
        ? `Required for app installs — used for the app URL, SSL and cloud-init.${generate}`
        : `Used for app URLs and cloud-init. Leave unchanged to keep the current value.${generate}`
    },
    // The API supplies the offer (suggested_domain) — still offered during a
    // rebuild even though a domain is already set, since the customer may want a
    // fresh one. Nothing is derived here, so the no-ip domain stays configurable
    // per environment rather than compiled into the client. @since GG-994
    suggestedDomain () {
      return this.server.suggested_domain || ''
    },
    canRebuild () {
      if (this.mode === 'choose' && !this.selection) return false
      if (this.domainRequired && !this.domainInput.trim()) return false
      return true
    },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    generateDomain () {
      if (!this.suggestedDomain) {
        this.reportError(new Error('A free subdomain isn’t available yet — try again in a moment.'))
        return
      }
      this.domainInput = this.suggestedDomain
    },
    async loadCatalog () {
      try {
        this.loadingCatalog = true
        const data = await kvm.getCatalog(this.server.id)
        this.catalog = {
          operating_systems: data.operating_systems || [],
          apps: data.apps || [],
        }
      } catch (e) {
        // Catalog is only needed for the "different OS/App" options; reinstalling
        // the current OS still works without it.
        this.reportError(e)
      } finally {
        this.loadingCatalog = false
      }
    },
    closeConfirm () {
      this.confirmDialog = false
      this.confirmText = ''
    },
    async rebuild () {
      if (this.confirmText !== 'REBUILD') return
      let choice = {}
      if (this.mode === 'choose' && this.selection) {
        const s = this.selection
        choice = s.kind === 'os' ? { os: s.slug } : { app: s.slug }
        if (s.options && Object.keys(s.options).length) choice.options = s.options
      }
      // Send domain only when changed ('' clears it; unchanged → omit to keep current).
      const trimmed = this.domainInput.trim()
      if (trimmed !== (this.server.domain || '')) choice.domain = trimmed
      choice.ssh_key_ids = this.sshKeyIds
      try {
        this.submitting = true
        await kvm.reprovision(this.server.id, choice)
        this.reportSuccess('Rebuild started — your server is being reinstalled.')
        this.closeConfirm()
        // Send the user back to the overview; the service-state gate there shows
        // the provisioning spinner until the rebuilt VM is back up.
        this.$router.push({ name: 'hosting_kvm_info', params: { hostingid: this.server.id } })
          .catch(() => { /* ignore redundant navigation */ })
        if (this.onRefresh) this.onRefresh()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.submitting = false
      }
    },
  },
  created () {
    // Default the injected-key selection to the service's current keys.
    this.sshKeyIds = (this.server.ssh_keys || []).map(k => k.id)
    // Pre-fill the domain with the current value so "unchanged" keeps it.
    this.domainInput = this.server.domain || ''
    this.loadCatalog()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-secondary {
  @include kvm-secondary-text;
}

.kvm-sr-only {
  @include kvm-sr-only;
}

// Visible focus ring on every button (GGBtn's root <button> inherits this
// component's scope id, so the class selector reaches it without ::v-deep).
.gg-button:focus-visible {
  @include kvm-focus-ring;
}

// Brand-green TEXT/outlined controls — "Generate" (outlined) and the dialog
// "Cancel" (text) render green-on-white at only ~3.15:1. Recolor the label to the
// AA-safe green (#0a7c3c light / #3db14e dark); any outlined border stays brand
// green (a 3:1 UI boundary is sufficient).
.kvm-aa-text {
  @include kvm-text-green-aa;
}

// KNOWN ISSUE: the amber warning alert shipped white-on-amber text (≈1.7:1).
// Force dark text + icon for AA; the surface is amber in BOTH themes (light
// #FFB70F ≈ 9.2:1, dark #FB8C00 ≈ 6.8:1 against #212121), so one rule covers both.
.kvm-rebuild__warning {
  &.v-alert {
    color: #212121 !important;
  }
  ::v-deep .v-alert__content,
  ::v-deep .v-alert__content strong,
  ::v-deep .v-alert__icon {
    color: #212121 !important;
  }
}

// Destructive rebuild button — brand error red (#FF3739) is only ~3.6:1 with white
// text. Darken to keep the danger cue while meeting AA (white on #c62828 ≈ 5.6:1).
.kvm-danger-btn.v-btn:not(.v-btn--disabled) {
  background-color: #c62828 !important;
  border-color: #c62828 !important;
  color: #fff !important;
}
</style>
